import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import {
  clearAggregationSettlement,
  markAggregationSettledForGame,
} from './aggregation-settlement-tracker';
import fetchAggregationData from './game/fetch-aggregation-data';
import handleAggregationData from './helpers/handle-aggreagation-data';
import { dispatchSWJData } from './swj/dispatch-swj-data';
import { useStore } from '@/store/useStore';
import { APIServiceType } from '@/api/types';

/**
 * Launch-time aggregation: refetch when session/game reset counters or `gameUid` change.
 * GLS uses `usePrismic` and does not wait on this payload — refresh the store only.
 */
const useAggregationData = (gameUid: string | null) => {
  const gameResetRevision = useStore((s) => s.gameResetRevision);
  const sessionResetRevision = useStore((s) => s.sessionResetRevision);
  const hasActiveSession = useStore(
    (s) => s.userSessionContext.hasActiveSession,
  );

  const queryKey = useMemo(() => ['aggregation', gameUid] as const, [gameUid]);

  const query = useQuery({
    queryKey,
    queryFn: () =>
      fetchAggregationData(gameUid as string, handleAggregationData),
    meta: { apiService: APIServiceType.AGGREGATION },
    // Fetch orchestration lives in the effect below (gameUid + reset revisions).
    // Disabling automatic fetch avoids duplicate cycles when the key/enabled flip
    // and the effect both trigger refetch().
    enabled: false,
    gcTime: Infinity,
  });

  const { refetch } = query;

  useEffect(() => {
    clearAggregationSettlement();
    dispatchSWJData(undefined, undefined);

    if (!gameUid || !hasActiveSession) {
      return;
    }

    let active = true;

    void refetch().then(
      (result) => {
        if (active) {
          markAggregationSettledForGame(gameUid, result.isSuccess);
        }
      },
      () => {
        if (active) {
          markAggregationSettledForGame(gameUid, false);
        }
      },
    );

    return () => {
      active = false;
    };
  }, [
    gameResetRevision,
    sessionResetRevision,
    gameUid,
    refetch,
    hasActiveSession,
  ]);
};

export default useAggregationData;
